import { z } from 'zod';
import { IdSchema, MetadataOnlyIntentSchema } from './horde-event-schema-helpers';
import {
    AiTargetCandidateSchema,
    EffectInstanceSchema,
    FortifiedMarkerSchema,
    HordeUnitDestructionCauseSchema,
    MaterializedRandomResultSchema,
    RecordedTableFactSchema,
} from './horde-session';

export const HordeIntentPayloadSchemasB = {
    END_ROUND: MetadataOnlyIntentSchema,
    EXPIRE_EFFECT: MetadataOnlyIntentSchema.extend({
        effectId: IdSchema,
    }),
    FAIL_MISSION_ACTION: MetadataOnlyIntentSchema.extend({
        actionId: IdSchema,
        reason: z.string().min(1),
    }),
    FAIL_SECRET_OBJECTIVE: MetadataOnlyIntentSchema.extend({
        playerId: IdSchema,
        reason: z.string().min(1),
        vaultRevision: z.number().int().nonnegative(),
    }),
    FINALIZE_SETUP: MetadataOnlyIntentSchema,
    FORTIFY_OBJECTIVE: MetadataOnlyIntentSchema.extend({
        marker: FortifiedMarkerSchema,
        objectiveId: IdSchema,
    }),
    INCREMENT_MISSION_TALLY: MetadataOnlyIntentSchema.extend({
        amount: z.number(),
        key: z.string().min(1),
        secondaryCardId: IdSchema,
    }),
    INCREMENT_PRIVATE_SECRET_TALLY: MetadataOnlyIntentSchema.extend({
        amount: z.number(),
        key: z.string().min(1),
        playerId: IdSchema,
        vaultRevision: z.number().int().nonnegative(),
    }),
    MANUAL_END_GAME: MetadataOnlyIntentSchema.extend({
        reason: z.string().min(1),
    }),
    MARK_MISSION_TARGET: MetadataOnlyIntentSchema.extend({
        secondaryCardId: IdSchema,
        targetRef: IdSchema,
    }),
    MARK_VOTANN_ENEMY_UNIT: MetadataOnlyIntentSchema.extend({
        hordeUnitId: IdSchema,
        playerUnitRef: IdSchema,
    }),
    MATERIALIZE_DECK_ORDER: MetadataOnlyIntentSchema.extend({
        deckId: z.enum(['misery', 'secondary', 'secretObjective']),
        order: z.array(IdSchema),
        randomResult: MaterializedRandomResultSchema,
        vaultRevision: z.number().int().nonnegative(),
    }),
    PLAYER_FINISHED_RESUPPLY: MetadataOnlyIntentSchema.extend({
        playerId: IdSchema,
    }),
    PURCHASE_RESUPPLY: MetadataOnlyIntentSchema.extend({
        baseCost: z.number().int().nonnegative(),
        cost: z.number().int().nonnegative(),
        playerId: IdSchema,
        purchaseId: IdSchema,
        purchaseRecordId: IdSchema,
        tags: z.array(z.string()),
        targetRefs: z.array(IdSchema),
        useLimit: z.number().int().positive().nullable(),
    }),
    QUEUE_FORCED_CARD: MetadataOnlyIntentSchema.extend({
        cardId: IdSchema,
        deckId: z.enum(['misery', 'secondary']),
        effect: EffectInstanceSchema,
    }),
    RECORD_SPAWN_DICE: MetadataOnlyIntentSchema.extend({
        dice: z.array(z.number().int().positive()),
        naturalTwo: z.boolean(),
        randomResult: MaterializedRandomResultSchema,
        unmodifiedTotal: z.number().int(),
    }),
    REDEEM_SECONDARY_REWARD_CARD: MetadataOnlyIntentSchema.extend({
        playerId: IdSchema,
        rewardCardId: IdSchema,
        targetId: IdSchema,
    }),
    REMOVE_BATTLEFIELD_MARKER: MetadataOnlyIntentSchema.extend({
        markerId: IdSchema,
        reason: z.string().min(1),
    }),
    REPORT_ACTION_COMPLETION_FACTS: MetadataOnlyIntentSchema.extend({
        actionId: IdSchema,
        facts: z.array(RecordedTableFactSchema),
    }),
    REPORT_BATTLESHOCK: MetadataOnlyIntentSchema.extend({
        battleShocked: z.boolean(),
        unitId: IdSchema,
    }),
    REPORT_CHARGE_ELIGIBILITY_FACTS: MetadataOnlyIntentSchema.extend({
        canCharge: z.boolean(),
        candidates: z.array(AiTargetCandidateSchema),
        unitId: IdSchema,
    }),
    REPORT_DISEMBARK_FACTS: MetadataOnlyIntentSchema.extend({
        disembarkEnablesCharge: z.boolean(),
        firingDeckInsufficient: z.boolean(),
        unitId: IdSchema,
        visibleEnemyInRange: z.boolean(),
    }),
    REPORT_DISTANCE_ORDER: MetadataOnlyIntentSchema.extend({
        candidateIds: z.array(IdSchema),
        unitId: IdSchema,
    }),
    REPORT_ENGAGEMENT_STATUS: MetadataOnlyIntentSchema.extend({
        beganPhaseEngaged: z.boolean(),
        canChargeAfterFallBack: z.boolean(),
        canShootAfterFallBack: z.boolean(),
        unitId: IdSchema,
    }),
    REPORT_HORDE_UNIT_DESTROYED: MetadataOnlyIntentSchema.extend({
        cause: HordeUnitDestructionCauseSchema,
        responsiblePlayerId: IdSchema.nullable(),
        unitId: IdSchema,
    }),
    REPORT_LEGAL_SHOOTING_TARGETS: MetadataOnlyIntentSchema.extend({
        candidates: z.array(AiTargetCandidateSchema),
        unitId: IdSchema,
        weaponGroupId: IdSchema.nullable(),
    }),
    REPORT_MOVE_BONUS_CONSEQUENCE: MetadataOnlyIntentSchema.extend({
        significantBonusLost: z.boolean(),
        unitId: IdSchema,
    }),
    REPORT_OBJECTIVE_CONTROL: MetadataOnlyIntentSchema.extend({
        controlledBy: z.enum(['horde', 'players', 'neither', 'contested', 'unknown']),
        controllerPlayerIds: z.array(IdSchema),
        fact: RecordedTableFactSchema,
        objectiveId: IdSchema,
    }),
    REPORT_PLAYER_SURVIVAL: MetadataOnlyIntentSchema.extend({
        playerId: IdSchema,
        survival: z.enum(['surviving', 'eliminated']),
    }),
    REPORT_PLAYER_UNIT_DESTROYED: MetadataOnlyIntentSchema.extend({
        playerId: IdSchema,
        playerUnitRef: IdSchema,
    }),
    REPORT_UNITS_IN_SPAWN_ZONE: MetadataOnlyIntentSchema.extend({
        hordeUnitIds: z.array(IdSchema),
        playerUnitRefs: z.array(IdSchema),
        zoneId: IdSchema,
    }),
    REPORT_VISIBLE_ENEMIES: MetadataOnlyIntentSchema.extend({
        candidates: z.array(AiTargetCandidateSchema),
        unitId: IdSchema,
    }),
    REPORT_VISIBLE_OBJECTIVES: MetadataOnlyIntentSchema.extend({
        candidates: z.array(AiTargetCandidateSchema),
        unitId: IdSchema,
    }),
    RESHUFFLE_DECK: MetadataOnlyIntentSchema.extend({
        deckId: z.enum(['misery', 'secondary', 'secretObjective']),
        order: z.array(IdSchema),
        randomResult: MaterializedRandomResultSchema,
        vaultRevision: z.number().int().nonnegative(),
    }),
} satisfies Record<string, z.ZodType>;
